import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Divider,
  Typography,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
} from "@material-ui/core";

import SortIcon from "@material-ui/icons/Sort";
import SortByAlphaIcon from "@material-ui/icons/SortByAlpha";
import SearchBar from "material-ui-search-bar";
import {
  fetchQuAPIVaultAPI,
  createQUApiVaultKey,
  editQUApiVaultKey,
  deleteQUApiVaultKey,
} from "views/APIs/APIUtility";
import { quCreateColor } from "assets/jss/material-dashboard-pro-react";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import NotificationContext from "views/Components/Context";
import VaultCard from "views/Components/VaultCard";
import CreateCourseCard from "views/Components/CreateCourseCard";
import ModelSelectionModal from "views/Components/ModelSelectionModal";
import Pagination from "components/Pagination/Pagination.js";

import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { makeStyles } from "@material-ui/core/styles";
import WaveTitle from "./SolutionGenerator/WaveTitle";

const useStyles = makeStyles({
  ...styles,
});

export default function NewQuApiVault() {
  const { showNotification } = useContext(NotificationContext);
  const classes = useStyles();

  const [searchValue, setSearchValue] = useState("");
  const [initialKeys, setInitialKeys] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState({ loading: false, message: "" });
  const [createAssetModal, setCreateAssetModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const assetsPerPage = 5;

  const [modelDetails, setModelDetails] = useState({
    company: "",
    model: "",
    apiKey: "",
    name: "",
    description: "",
    key_type: "none", // new
  });

  const [alphabeticallyInc, setAlphabeticallyInc] = useState(false);
  const [ascendingOrder, setAscendingOrder] = useState(false);

  // Derived helpers for key_type rules
  const hasDefaultKey = useMemo(
    () => apiKeys.some((k) => (k.key_type || "none") === "default"),
    [apiKeys]
  );
  const hasBackupKey = useMemo(
    () => apiKeys.some((k) => (k.key_type || "none") === "backup"),
    [apiKeys]
  );

  // Decide next key type on creation (enforce the rules)
  const getNextCreateKeyType = () => {
    if (apiKeys.length === 0) return "default";
    if (hasDefaultKey && !hasBackupKey) return "backup";
    return "none";
  };

  // Pagination
  const indexOfLastAsset = currentPage * assetsPerPage;
  const indexOfFirstAsset = indexOfLastAsset - assetsPerPage;
  const currentAssets = apiKeys.slice(indexOfFirstAsset, indexOfLastAsset);
  const totalPages = Math.ceil(apiKeys.length / assetsPerPage);

  const pages = [];
  if (totalPages > 1) {
    pages.push({
      text: "PREV",
      disabled: currentPage === 1,
      onClick: () => setCurrentPage((prev) => Math.max(prev - 1, 1)),
    });
    for (let i = 1; i <= totalPages; i++) {
      pages.push({
        text: i,
        active: i === currentPage,
        onClick: () => setCurrentPage(i),
      });
    }
    pages.push({
      text: "NEXT",
      disabled: currentPage === totalPages,
      onClick: () => setCurrentPage((prev) => Math.min(prev + 1, totalPages)),
    });
  }

  useEffect(() => {
    const fetchKeys = async () => {
      setLoading({ loading: true, message: "Loading keys..." });
      try {
        const fetchedKeys = await fetchQuAPIVaultAPI();
        // normalize missing legacy records to key_type: 'none'
        const normalized = (fetchedKeys || []).map((k) => ({
          ...k,
          key_type: k.key_type || "none",
        }));
        setApiKeys(normalized);
        setInitialKeys(normalized);
      } catch (error) {
        showNotification("Error fetching keys", "danger");
      } finally {
        setLoading({ loading: false, message: "" });
      }
    };
    fetchKeys();
  }, [showNotification]);

  const handleDeleteAsset = async (asset) => {
    setLoading({ loading: true, message: "Deleting key..." });
    try {
      const formData = new FormData();
      formData.append("key_id", asset._id);
      await deleteQUApiVaultKey(formData);
      setApiKeys((prev) => prev.filter((api) => api._id !== asset._id));
      showNotification("Key deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting key:", error);
      showNotification("Error deleting key", "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  const handleUpdateAsset = async (asset) => {
    setLoading({ loading: true, message: "Updating key..." });

    // Ensure we always send the current key_type for this asset
    const existing = apiKeys.find((a) => a._id === asset._id);
    const keyTypeToPersist = asset.key_type || existing?.key_type || "none";

    const formData = new FormData();
    formData.append("key_id", asset._id);
    formData.append("name", asset.name);
    formData.append("description", asset.description);
    formData.append("key", asset.new_api_key);
    formData.append("model", asset.model || "");
    formData.append("key_type", keyTypeToPersist); // NEW: persist key_type

    try {
      await editQUApiVaultKey(formData);
      setApiKeys((prev) =>
        prev.map((api) =>
          api._id === asset._id
            ? {
                ...api,
                name: asset.name,
                description: asset.description,
                key: asset.new_api_key,
                model: asset.model,
                key_type: keyTypeToPersist,
              }
            : api
        )
      );
      showNotification("Key updated successfully", "success");
    } catch (error) {
      console.error("Error updating key:", error);
      showNotification("Error updating key", "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  // Search
  const searchInputValue = (e) => {
    setSearchValue(e);
    if (e.length === 0) {
      setApiKeys(initialKeys);
      return;
    }
    const lower = e.toLowerCase();
    const newList = initialKeys.filter((asset) => {
      const nameMatch = asset.name && asset.name.toLowerCase().includes(lower);
      const modelMatch =
        asset.model && asset.model.toLowerCase().includes(lower);
      const typeMatch = (asset.key_type || "none")
        .toLowerCase()
        .includes(lower);
      return nameMatch || modelMatch || typeMatch;
    });
    setApiKeys(newList);
    setCurrentPage(1);
  };

  const handleCreateAsset = async () => {
    try {
      const enforcedType = getNextCreateKeyType();

      const formData = new FormData();
      formData.append("model", modelDetails.model || "");
      formData.append("key", modelDetails.apiKey);
      formData.append("name", modelDetails.name);
      formData.append("description", modelDetails.description);
      formData.append("company", modelDetails.company);
      formData.append("type", "llms");
      formData.append("key_type", enforcedType); // NEW: enforced create rule

      const response = await createQUApiVaultKey(formData);
      const message = response.message;
      const newApiKey = {
        ...(response.response || {}),
        key_type:
          (response.response && response.response.key_type) || enforcedType,
      };

      setApiKeys((prevApiKeys) => [...prevApiKeys, newApiKey]);
      showNotification(
        message || `Key created as ${enforcedType.toUpperCase()}`,
        "success"
      );
      setCreateAssetModal(false);
      setModelDetails({
        company: "",
        model: "",
        apiKey: "",
        name: "",
        description: "",
        key_type: "none",
      });
    } catch (err) {
      console.error(err);
      showNotification("Error creating key", "danger");
    }
  };

  // Helpers to find current role holders
  const getCurrentDefault = () =>
    apiKeys.find((k) => (k.key_type || "none") === "default");
  const getCurrentBackup = () =>
    apiKeys.find((k) => (k.key_type || "none") === "backup");

  // Minimal utility to persist key_type only (uses updated edit endpoint)
  const persistKeyType = async (keyId, keyType, targetKey) => {
    const formData = new FormData();
    formData.append("key_id", keyId);
    formData.append("key_type", keyType); // backend was updated to accept this
    formData.append("model", "");
    formData.append("name", targetKey.name || "");
    formData.append("description", targetKey.description || "");
    return editQUApiVaultKey(formData);
  };

  // Optimistically update local state for one key
  const patchLocalKeyType = (keyId, nextType) => {
    setApiKeys((prev) =>
      prev.map((k) => (k._id === keyId ? { ...k, key_type: nextType } : k))
    );
  };

  // Make this key the DEFAULT (demotes old default -> "none")
  const setAsDefault = async (targetKey) => {
    if ((targetKey.key_type || "none") === "default") {
      showNotification("This key is already the default.", "info");
      return;
    }
    setLoading({ loading: true, message: "Setting default key..." });
    const prevDefault = getCurrentDefault();

    try {
      // Optimistic UI
      if (prevDefault) patchLocalKeyType(prevDefault._id, "none");
      patchLocalKeyType(targetKey._id, "default");

      // Persist
      if (prevDefault)
        await persistKeyType(prevDefault._id, "none", prevDefault);
      await persistKeyType(targetKey._id, "default", targetKey);

      showNotification("Default key updated.", "success");
    } catch (e) {
      // Rollback (simple refetch could also work)
      if (prevDefault) patchLocalKeyType(prevDefault._id, "default");
      patchLocalKeyType(targetKey._id, targetKey.key_type || "none");
      console.error(e);
      showNotification("Failed to set default key.", "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  // Make this key the BACKUP (demotes old backup -> "none")
  const setAsBackup = async (targetKey) => {
    const isDefault = (targetKey.key_type || "none") === "default";
    const isBackup = (targetKey.key_type || "none") === "backup";
    if (isBackup) {
      showNotification("This key is already the backup.", "info");
      return;
    }
    if (isDefault) {
      showNotification("The default key cannot also be the backup.", "warning");
      return;
    }
    if (!hasDefaultKey) {
      // Business rule: backup only makes sense once a default exists
      showNotification("Create or set a default key first.", "warning");
      return;
    }

    setLoading({ loading: true, message: "Setting backup key..." });
    const prevBackup = getCurrentBackup();

    try {
      // Optimistic UI
      if (prevBackup) patchLocalKeyType(prevBackup._id, "none");
      patchLocalKeyType(targetKey._id, "backup");

      // Persist
      if (prevBackup) await persistKeyType(prevBackup._id, "none", prevBackup);
      await persistKeyType(targetKey._id, "backup", targetKey);

      showNotification("Backup key updated.", "success");
    } catch (e) {
      // Rollback
      if (prevBackup) patchLocalKeyType(prevBackup._id, "backup");
      patchLocalKeyType(targetKey._id, targetKey.key_type || "none");
      console.error(e);
      showNotification("Failed to set backup key.", "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  const sortCards = () => {
    const sortedAssets = [...apiKeys].sort((a, b) => {
      const dateA = new Date(parseInt(a._id.toString().slice(0, 8), 16) * 1000);
      const dateB = new Date(parseInt(b._id.toString().slice(0, 8), 16) * 1000);
      return ascendingOrder ? dateA - dateB : dateB - dateA;
    });
    setApiKeys(sortedAssets);
    setAscendingOrder(!ascendingOrder);
  };

  const sortCardsAlphabetically = () => {
    const sortedAssets = [...apiKeys].sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return alphabeticallyInc
        ? nameB.localeCompare(nameA)
        : nameA.localeCompare(nameB);
    });
    setApiKeys(sortedAssets);
    setAlphabeticallyInc(!alphabeticallyInc);
  };

  return (
    <>
      <ModelSelectionModal
        open={createAssetModal}
        handleModelSelectionModalClose={() => setCreateAssetModal(false)}
        handleCreateAsset={handleCreateAsset}
        modelDetails={modelDetails}
        setModelDetails={setModelDetails}
      />
      <Divider style={{ marginBottom: "1rem" }} />
      <Typography variant="h4">
        <WaveTitle text="QuAPI Vault" />
      </Typography>
      <Typography variant="body1" style={{  }}>
        Here you can manage your API keys. You can create new keys, edit or
        delete existing keys.
      </Typography>
      <Divider style={{ marginTop: "1rem", marginBottom: "1rem" }} />

      <GridContainer>
        <GridItem xs={12} sm={8} md={8} lg={10}>
          <SearchBar
            value={searchValue}
            onChange={searchInputValue}
            onRequestSearch={searchInputValue}
            placeholder={`Search by name, model, or type`}
          />
        </GridItem>
        <GridItem xs={12} sm={4} md={4} lg={2} style={{ textAlign: "center" }}>
          <Button
            color="quCreateColor"
            fullWidth
            onClick={() => setCreateAssetModal(true)}
          >
            Create API Key
          </Button>
        </GridItem>
      </GridContainer>

      {loading.loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress
            color="quCreateColor"
            className={classes.loaderClass}
          />
          <Typography
            variant="h6"
            style={{ marginLeft: 16, textTransform: "none" }}
          >
            {loading.message}
          </Typography>
        </Box>
      ) : (
        <GridContainer>
          <GridItem
            item
            xs={12}
            style={{
              marginTop: "20px",
              marginBottom: "20px",
              display: "flex",
              gap: "20px",
            }}
          >
            <Tooltip title="Sort by creation date" placement="top">
              <IconButton
                color="#20677b"
                style={{
                  backgroundColor: ascendingOrder ? quCreateColor : "#f5f5f5",
                }}
              >
                <SortIcon onClick={sortCards} style={{ cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sort alphabetically" placement="top">
              <IconButton
                style={{
                  backgroundColor: alphabeticallyInc
                    ? quCreateColor
                    : "#f5f5f5",
                }}
              >
                <SortByAlphaIcon
                  onClick={sortCardsAlphabetically}
                  style={{ cursor: "pointer" }}
                />
              </IconButton>
            </Tooltip>
          </GridItem>

          {currentAssets.length === 0 && (
            <GridItem
              xs={12}
              sm={12}
              md={12}
              lg={12}
              style={{ marginTop: "20px" }}
            >
              <Box display="flex" justifyContent="center">
                <Typography variant="h5" textAlign="center">
                  Create or Review your API Keys to get started.
                </Typography>
              </Box>
            </GridItem>
          )}

          {currentAssets.map((asset, index) => (
            <GridItem key={index} xs={12} sm={6} md={6} lg={4}>
              <VaultCard
                apiKeyObject={asset}
                handleDeleteAsset={handleDeleteAsset}
                handleUpdateAsset={handleUpdateAsset}
                onMakeDefault={() => setAsDefault(asset)}
                onMakeBackup={() => setAsBackup(asset)}
                hasDefaultKey={hasDefaultKey}
                hasBackupKey={hasBackupKey}
              />
            </GridItem>
          ))}

          <GridItem xs={12} sm={6} md={6} lg={4}>
            <CreateCourseCard onClick={() => setCreateAssetModal(true)} />
          </GridItem>
        </GridContainer>
      )}

      {totalPages > 1 && (
        <GridContainer
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
          }}
        >
          <Pagination pages={pages} color="quCreateColor" />
        </GridContainer>
      )}
    </>
  );
}
